/**
 * Who is this portal for? (Phase SC-4b)
 *
 * The portal is one build serving every tenant, and *which vertical it is
 * serving* decides the nav, the vocabulary and which Home screen mounts.
 * Until this read existed the answer came from a hardcoded map of the three
 * seeded slugs in `lib/institution.ts`, which silently fell back to
 * **school** — so onboarding a real study centre would have handed its
 * learners a school portal.
 *
 * `GET /tenants/{slug}/public-profile` is anonymous (it has to paint the
 * login screen) and carries the branding block inline, so a cold load is
 * one round-trip. Cached for 5 minutes like the branding read: a rename or
 * a re-brand lands on the next cold load, and tab navigation never
 * re-fetches.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { publicApi } from "@/lib/api";
import type { BrandingPublic } from "@/lib/branding/types";
import type { Vertical } from "@/lib/institution";

export type TenantStatus = "active" | "suspended" | "archived";

export interface TenantPublicProfile {
  slug: string;
  name: string;
  vertical: Vertical;
  status: TenantStatus;
  branding: BrandingPublic;
  learner_app_offered: boolean;
}

export function useTenantProfile(
  slug: string | null | undefined,
): UseQueryResult<TenantPublicProfile> {
  return useQuery<TenantPublicProfile>({
    queryKey: ["public-tenant-profile", slug],
    queryFn: async () => {
      const res = await publicApi.get<TenantPublicProfile>(
        `/tenants/${slug as string}/public-profile`,
      );
      return res.data;
    },
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
    // A wrong slug is a permanent 404, not a blip: retrying it just delays
    // the "we couldn't find this institution" screen.
    retry: false,
  });
}
